import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
    Activity,
    AlertTriangle,
    Bot,
    CheckCircle2,
    Clock3,
    Database,
    FileOutput,
    Play,
    RefreshCw,
    Save,
    ShieldCheck,
    SlidersHorizontal,
    Users
} from 'lucide-react';
import ScreenLayout from './ScreenLayout';
import { useAuth } from '../context/AuthContext';
import { getAdminStatus, runDailyGeneration, savePromptSetting } from '../services/adminService';

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value || 0);
const formatTime = (value) => value ? new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
}).format(new Date(value)) : '—';
const compactJson = (value) => value && Object.keys(value).length ? JSON.stringify(value) : '—';

const StatusPill = ({ value }) => (
    <span className={`admin-status admin-status-${value || 'unknown'}`}>{value || 'unknown'}</span>
);

const EmptyRow = ({ colSpan, children = 'No records yet.' }) => (
    <tr><td className="admin-empty-cell" colSpan={colSpan}>{children}</td></tr>
);

const Section = ({ icon: Icon, title, description, action, children }) => (
    <section className="admin-section">
        <header className="admin-section-head">
            <span className="admin-section-icon"><Icon size={18} /></span>
            <div>
                <h2>{title}</h2>
                {description && <p>{description}</p>}
            </div>
            {action && <div className="admin-section-action">{action}</div>}
        </header>
        <div className="admin-section-body">{children}</div>
    </section>
);

const AccessMessage = ({ title, message, signIn }) => (
    <ScreenLayout title="Admin Operations" icon={Activity}>
        <section className="admin-access">
            <ShieldCheck size={28} />
            <p className="shared-kicker">Private operations</p>
            <h1>{title}</h1>
            <p>{message}</p>
            {signIn && <Link to="/login" className="admin-button admin-button-primary">Sign in with Google</Link>}
        </section>
    </ScreenLayout>
);

const AdminDashboard = () => {
    const { configured, user, isAdmin, adminLoading } = useAuth();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [daysAhead, setDaysAhead] = useState(3);
    const [running, setRunning] = useState(false);
    const [runMessage, setRunMessage] = useState('');
    const [promptDrafts, setPromptDrafts] = useState({});
    const [savingPrompt, setSavingPrompt] = useState('');

    const refresh = useCallback(async () => {
        if (!isAdmin) return;
        setLoading(true);
        setError('');
        try {
            const next = await getAdminStatus();
            setStatus(next);
            setPromptDrafts(Object.fromEntries((next.promptCatalog || []).map((prompt) => [
                prompt.kind,
                {
                    enabled: prompt.setting?.enabled !== false,
                    system_addendum: prompt.setting?.system_addendum || '',
                    prompt_addendum: prompt.setting?.prompt_addendum || '',
                    model: prompt.setting?.model || '',
                    effort: prompt.setting?.effort || ''
                }
            ])));
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setLoading(false);
        }
    }, [isAdmin]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const runDaily = async () => {
        setRunning(true);
        setRunMessage('');
        try {
            const result = await runDailyGeneration(daysAhead);
            const generated = result.results.filter((item) => item.status === 'generated').length;
            const existing = result.results.filter((item) => item.status === 'exists').length;
            const failed = result.results.filter((item) => item.status === 'error').length;
            setRunMessage(`${generated} generated, ${existing} already covered, ${failed} failed.`);
            await refresh();
        } catch (requestError) {
            setRunMessage(requestError.message);
        } finally {
            setRunning(false);
        }
    };

    const updatePromptDraft = (kind, field, value) => {
        setPromptDrafts((current) => ({
            ...current,
            [kind]: { ...current[kind], [field]: value }
        }));
    };

    const savePrompt = async (kind) => {
        setSavingPrompt(kind);
        setError('');
        try {
            await savePromptSetting(kind, promptDrafts[kind]);
            await refresh();
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setSavingPrompt('');
        }
    };

    const bankDates = useMemo(() => new Set((status?.bank || []).map((row) => row.date)).size, [status]);

    if (!configured) {
        return <AccessMessage title="Accounts are not configured." message="Add the Supabase environment variables before using private diagnostics." />;
    }
    if (!user) {
        return <AccessMessage title="Sign in required." message="Use the allowlisted Google account to open operations." signIn />;
    }
    if (adminLoading) {
        return <AccessMessage title="Checking admin access…" message="Confirming this account against the private server allowlist." />;
    }
    if (!isAdmin) {
        return <AccessMessage title="Admin access required." message="This account is signed in but its immutable user ID is not on the server admin allowlist." />;
    }

    const summary = status?.summary || {};

    return (
        <ScreenLayout title="Admin Operations" icon={Activity}>
            <section className="admin-hero">
                <div>
                    <p className="shared-kicker"><ShieldCheck size={14} /> Private diagnostics</p>
                    <h1>Know what the app is doing.</h1>
                    <p>AI calls, future-day coverage, user activity, prompt controls, errors, and downstream premium signals in one place.</p>
                </div>
                <button className="admin-button admin-button-ghost" onClick={refresh} disabled={loading}>
                    <RefreshCw size={16} className={loading ? 'admin-spin' : ''} />
                    {loading ? 'Refreshing' : 'Refresh'}
                </button>
            </section>

            <div className="admin-privacy-note">
                <ShieldCheck size={17} />
                <span><strong>Data-minimized diagnostics.</strong> Prayer context, journal content, rendered prompts, and generated devotional bodies are excluded. Premium and commerce intent payloads are retained.</span>
            </div>

            {error && <div className="admin-alert"><AlertTriangle size={17} /> {error}</div>}
            {Object.keys(status?.diagnosticErrors || {}).length > 0 && (
                <div className="admin-alert">
                    <AlertTriangle size={17} />
                    Some diagnostics need the latest <code>supabase/schema.sql</code>: {Object.entries(status.diagnosticErrors).map(([key, value]) => `${key}: ${value}`).join(' · ')}
                </div>
            )}
            {(summary.deadLetteredCrmEvents || 0) > 0 && (
                <div className="admin-alert">
                    <AlertTriangle size={17} />
                    {formatNumber(summary.deadLetteredCrmEvents)} CRM event(s) are dead-lettered after 10 failed Salesforce attempts. Fix the underlying issue before using the audited admin reset endpoint.
                </div>
            )}

            <div className="admin-summary-grid">
                {[
                    ['Users', summary.totalUsers, `${summary.newUsersLast7d || 0} new · ${summary.activeUsersLast7d || 0} active / 7d`, Users],
                    ['AI jobs', summary.recentJobs, `${summary.succeeded || 0} succeeded · ${summary.failed || 0} failed`, Bot],
                    ['Future bank', summary.bankSlotsAhead, `${bankDates} dates covered`, Database],
                    ['Generated outputs', summary.generatedOutputs, 'Metadata only in this view', FileOutput],
                    ['Synced state keys', summary.syncedStateRecords, 'Values stay private', RefreshCw],
                    ['CRM queue', summary.unsyncedCrmEvents, `${summary.deadLetteredCrmEvents || 0} dead-lettered`, Activity]
                ].map(([label, value, detail, Icon]) => (
                    <article className="admin-summary-card" key={label}>
                        <span><Icon size={18} /></span>
                        <small>{label}</small>
                        <strong>{formatNumber(value)}</strong>
                        <p>{detail}</p>
                    </article>
                ))}
            </div>

            <Section
                icon={Play}
                title="Daily content bank"
                description="Fill daily verses and devotionals ahead of the cron. Existing dates are skipped."
                action={(
                    <div className="admin-run-controls">
                        <label>
                            Through
                            <select value={daysAhead} onChange={(event) => setDaysAhead(Number(event.target.value))}>
                                <option value={3}>3 days ahead</option>
                                <option value={7}>7 days ahead</option>
                                <option value={14}>14 days ahead</option>
                            </select>
                        </label>
                        <button className="admin-button admin-button-primary" onClick={runDaily} disabled={running}>
                            <Play size={15} /> {running ? 'Generating…' : 'Generate now'}
                        </button>
                    </div>
                )}
            >
                {runMessage && <p className="admin-run-message">{runMessage}</p>}
                <div className="admin-run-history">
                    {(status?.operationRuns || []).slice(0, 5).map((run) => (
                        <div key={run.id}>
                            <span><StatusPill value={run.status} /><strong>{run.source}</strong></span>
                            <span>{compactJson(run.summary)} · {formatTime(run.started_at)}</span>
                        </div>
                    ))}
                    {!status?.operationRuns?.length && <p className="admin-empty">No cron or admin runs recorded yet.</p>}
                </div>
                <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead><tr><th>Date</th><th>Kind</th><th>Status</th><th>Model</th><th>Created</th></tr></thead>
                        <tbody>
                            {(status?.bank || []).map((row) => (
                                <tr key={`${row.date}-${row.kind}`}>
                                    <td>{row.date}</td><td><code>{row.kind}</code></td><td><StatusPill value={row.status} /></td><td>{row.model || '—'}</td><td>{formatTime(row.created_at)}</td>
                                </tr>
                            ))}
                            {!status?.bank?.length && <EmptyRow colSpan={5}>No future bank slots found.</EmptyRow>}
                        </tbody>
                    </table>
                </div>
            </Section>

            <Section icon={SlidersHorizontal} title="Prompt catalog" description="Base templates live in code. Admin settings can disable a kind, choose model/effort overrides, or append reviewed instructions.">
                <div className="admin-prompt-grid">
                    {(status?.promptCatalog || []).map((prompt) => {
                        const draft = promptDrafts[prompt.kind] || {};
                        return (
                            <details className="admin-prompt-card" key={prompt.kind}>
                                <summary>
                                    <span><code>{prompt.kind}</code><small>{prompt.scope} · {formatNumber(prompt.maxTokens)} max tokens</small></span>
                                    <StatusPill value={draft.enabled === false ? 'disabled' : 'enabled'} />
                                </summary>
                                <div className="admin-prompt-body">
                                    <div className="admin-prompt-fields">
                                        <label className="admin-check">
                                            <input type="checkbox" checked={draft.enabled !== false} onChange={(event) => updatePromptDraft(prompt.kind, 'enabled', event.target.checked)} />
                                            Enabled
                                        </label>
                                        <label>Model override<input value={draft.model || ''} onChange={(event) => updatePromptDraft(prompt.kind, 'model', event.target.value)} placeholder="Use server default" /></label>
                                        <label>Effort override
                                            <select value={draft.effort || ''} onChange={(event) => updatePromptDraft(prompt.kind, 'effort', event.target.value)}>
                                                <option value="">Use server default</option><option value="low">low</option><option value="medium">medium</option><option value="high">high</option><option value="max">max</option>
                                            </select>
                                        </label>
                                    </div>
                                    <label>System addendum<textarea rows="3" value={draft.system_addendum || ''} onChange={(event) => updatePromptDraft(prompt.kind, 'system_addendum', event.target.value)} placeholder="Optional reviewed instructions appended to the base system prompt" /></label>
                                    <label>Prompt addendum<textarea rows="3" value={draft.prompt_addendum || ''} onChange={(event) => updatePromptDraft(prompt.kind, 'prompt_addendum', event.target.value)} placeholder="Optional reviewed instructions appended to each generated request" /></label>
                                    <button className="admin-button admin-button-primary" onClick={() => savePrompt(prompt.kind)} disabled={savingPrompt === prompt.kind}>
                                        <Save size={15} /> {savingPrompt === prompt.kind ? 'Saving…' : 'Save settings'}
                                    </button>
                                    <div className="admin-prompt-reference">
                                        <p><strong>Inputs:</strong> {prompt.inputFields.join(', ') || 'None'}</p>
                                        <details><summary>Base system prompt</summary><pre>{prompt.baseSystem}</pre></details>
                                        <details><summary>Sample rendered prompt</summary><pre>{prompt.samplePrompt}</pre></details>
                                        <details><summary>Output schema</summary><pre>{JSON.stringify(prompt.outputSchema, null, 2)}</pre></details>
                                    </div>
                                </div>
                            </details>
                        );
                    })}
                </div>
            </Section>

            <Section icon={Bot} title="Recent AI jobs" description={`${formatNumber(summary.tokensAcrossRecent)} tokens across the recent window. Raw prompts and user text are not retained.`}>
                <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead><tr><th>When</th><th>Kind</th><th>User / source</th><th>Status</th><th>Model</th><th>Tokens</th><th>Duration</th><th>Fingerprint / error</th></tr></thead>
                        <tbody>
                            {(status?.recentJobs || []).map((job) => {
                                const usage = job.meta?.usage;
                                const tokens = usage ? (usage.input_tokens || 0) + (usage.output_tokens || 0) : 0;
                                return (
                                    <tr key={job.id}>
                                        <td>{formatTime(job.created_at)}</td>
                                        <td><code>{job.kind}</code></td>
                                        <td>{job.user?.email || job.meta?.source || 'background'}</td>
                                        <td><StatusPill value={job.status} /></td>
                                        <td>{job.model || '—'}</td>
                                        <td>{tokens ? formatNumber(tokens) : '—'}</td>
                                        <td>{job.meta?.ms ? `${formatNumber(job.meta.ms)} ms` : '—'}</td>
                                        <td className="admin-cell-wrap">{job.error || job.meta?.requestFingerprint || '—'}</td>
                                    </tr>
                                );
                            })}
                            {!status?.recentJobs?.length && <EmptyRow colSpan={8}>No AI jobs recorded.</EmptyRow>}
                        </tbody>
                    </table>
                </div>
            </Section>

            <Section icon={Activity} title="Premium and downstream signals" description="Approved business telemetry from the CRM outbox. Payloads here contain offering and engagement signals, not prayer content.">
                <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead><tr><th>When</th><th>User</th><th>Event</th><th>Payload</th><th>Sync</th><th>Attempts</th><th>Last error</th></tr></thead>
                        <tbody>
                            {(status?.crmEvents || []).map((event) => (
                                <tr key={event.id}>
                                    <td>{formatTime(event.created_at)}</td>
                                    <td>{event.user?.email || event.user_id}</td>
                                    <td><code>{event.event_type}</code></td>
                                    <td className="admin-cell-wrap"><code>{compactJson(event.payload)}</code></td>
                                    <td><StatusPill value={event.dead_lettered_at ? 'dead-lettered' : event.synced_at ? 'synced' : 'pending'} /></td>
                                    <td>{event.sync_attempts}</td>
                                    <td className="admin-cell-wrap">{event.dead_letter_reason || event.last_sync_error || '—'}</td>
                                </tr>
                            ))}
                            {!status?.crmEvents?.length && <EmptyRow colSpan={7}>No downstream events recorded.</EmptyRow>}
                        </tbody>
                    </table>
                </div>
            </Section>

            <div className="admin-two-column">
                <Section icon={Users} title="Recent users" description="Identity and plan metadata.">
                    <div className="admin-list">
                        {(status?.profiles || []).map((profile) => (
                            <div className="admin-list-row" key={profile.id}>
                                <span><strong>{profile.full_name || profile.email || 'Unnamed user'}</strong><small>{profile.email}</small></span>
                                <span><StatusPill value={profile.plan} /><small>{formatTime(profile.created_at)}</small></span>
                            </div>
                        ))}
                        {!status?.profiles?.length && <p className="admin-empty">No users found.</p>}
                    </div>
                </Section>
                <Section icon={RefreshCw} title="State sync activity" description="Feature key and update time only; values remain private.">
                    <div className="admin-list">
                        {(status?.stateActivity || []).slice(0, 30).map((row, index) => (
                            <div className="admin-list-row" key={`${row.user_id}-${row.key}-${index}`}>
                                <span><strong>{row.key}</strong><small>{row.user?.email || row.user_id}</small></span>
                                <small>{formatTime(row.updated_at)}</small>
                            </div>
                        ))}
                        {!status?.stateActivity?.length && <p className="admin-empty">No synced state activity found.</p>}
                    </div>
                </Section>
            </div>

            <Section icon={FileOutput} title="Generated output ledger" description="Kinds and timestamps are shown for diagnostics; titles and generated content bodies stay private.">
                <div className="admin-table-wrap">
                    <table className="admin-table">
                        <thead><tr><th>When</th><th>User</th><th>Kind</th><th>Reviewed</th></tr></thead>
                        <tbody>
                            {(status?.generatedContent || []).map((item) => (
                                <tr key={item.id}>
                                    <td>{formatTime(item.created_at)}</td><td>{item.user?.email || item.user_id}</td><td><code>{item.kind}</code></td><td><StatusPill value={item.reviewed ? 'reviewed' : 'unreviewed'} /></td>
                                </tr>
                            ))}
                            {!status?.generatedContent?.length && <EmptyRow colSpan={4}>No generated outputs recorded.</EmptyRow>}
                        </tbody>
                    </table>
                </div>
            </Section>

            <footer className="admin-footer">
                <Clock3 size={15} /> Last snapshot: {formatTime(status?.generatedAt)}
                <span><CheckCircle2 size={15} /> Admin endpoints re-check the server allowlist on every request.</span>
            </footer>
        </ScreenLayout>
    );
};

export default AdminDashboard;
